import React, {useEffect, useRef, useState} from "react";
import {shallowEqual, useParams, useSelector} from "umi";
import { getReportDimensionInfo } from '@dbl/services/filter';
import {STRING_OPTION_PAGE_SIZE} from "@/pages/datalab/components/SortableDrag/configration";
import StringSelectorWithTag from "@/pages/datalab/components/StringSelectorWithTag";
import {useStringSelectorProps} from "@/pages/datalab/components/StringSelectorWithTag/hooks";
import {intersectionBy, isEmpty} from "lodash-es";
import {CheckOptionType} from '@/pages/datalab/components/FilterSelectorWithTag/type';

const StingFilter = (props: any, ref: any) => {
  const { filterInfo, innerFilterValueMapRef, system='datalab', setOpenStatus, setVisibleScope, eventEmitter, setErrorInfo } = props;
  const { filterName, i, dataset, column, otherInfo = {}, dependFilters, relevantFilters, } = filterInfo || {};
 
  const gameCode = useSelector((state) => state.datalab.gameCode, shallowEqual);
  const [stringValue, setStringValue] = useState({
    value: innerFilterValueMapRef.current?.[i]?.value ?? [],
    is_full: innerFilterValueMapRef.current?.[i]?.is_full ?? false,
    operation: innerFilterValueMapRef.current?.[i]?.operation ?? 'include',
    customValues: innerFilterValueMapRef.current?.[i]?.customValues ?? [],
  });
  const stringValueRef = useRef(stringValue);
  const initParamsRef = useRef<any | null>(null);
  
  useEffect(() => {
    stringValueRef.current = stringValue;
  }, [stringValue]);

  // const isInitRef = useRef<boolean>(true);

  // 经分承接页gameCode
  const { gamecode: dashboardGameCode }: any = useParams();
  
  const params = {
    game_code: system === 'datalab' ? gameCode : dashboardGameCode,
    table_id: dataset,
    dim_name: column,
    dim_has_query_table: true,
    page_size: STRING_OPTION_PAGE_SIZE,
    page_no: 1,
    system,
    filters: [],
    filter_key: i
  };
  if (otherInfo.sqlExpression) {
    // 自定义字段的请求下拉选项的传参
    // @ts-ignore
    params.custom_column = {
      custom_columns: [{
        name_cn: 'CustomField',
        column_type: 'float',
        name_en: 'CustomField',
        sql_expression: otherInfo.sqlExpression,
        accuracy: 2,
      }],
      metrics: [
        {
          table_code: 't0',
          table_id: '',
          column: 'CustomField',
          label: 'CustomField',
          label_en: 'CustomField',
          column_type: 'string',
          aggregate: '',
          accuracy: 2,
          to_date_format: '',
        }
      ]
    };
    params.table_id = dataset?.split('&')?.[0];
    params.chart_id = otherInfo.customColumnChartId ?? otherInfo.chartId;
    // @ts-ignore
    params.expression = otherInfo.sqlExpression;
  }
  if (dependFilters) {
    dependFilters.forEach((parent) => {
      innerFilterValueMapRef?.current?.[parent.i] && params.filters.push(innerFilterValueMapRef?.current?.[parent.i]);
    });
  }

  const {
    loading,
    error,
    options,
    getStringOptions,
    getStringOptionsAsync,
    onSearchKeywordChange,
    isClearSearchWord,
    lastParams,
  } = useStringSelectorProps({
    requestOptions: getReportDimensionInfo,
    params,
    // isInitRef,
    errorCallback: (info) => setErrorInfo(info),
    thenCallback: (options:CheckOptionType[], params) => {
      /**
       * 1. 页面初始化 (params使用 filterInfo, thencallback 内部 使用stringValue)
       * 2. 筛选器级联触发选项重新请求(params使用 filterInfo, thencallback 内部使用stringValue)
       * 3. 报表编辑 修改了筛选器, 触发重新请求 ( params使用 filterInfo, thencallback 内部使用stringValue, 但由于stringValue更新和filterInfo有时间差, 所以会计算不准确, 所以在useUpdateEffect中使用 setTimeout解决时间差)
       */

      /**
       *          虚拟版本配置     父级筛选
       * 情况1:    无             无
       * 情况2:    有             无
       * 情况3:    无             有
       * 情况4:    有             有
       */

      let result;

      // 有虚拟版本配置
      if (!isEmpty(filterInfo?.scope_value)) {
        
        const configOptions = filterInfo.scope_value.map(task=>(
          {
            value: task,
            text: task,
            title: task,
          }
        ));

        /**
         * 1. 有虚拟配置, 且有父级筛选, 需要将管理端配置的选项和 父级筛序后的结果进行交集
         * 2. 有虚拟配置, 后端搜索后, 需要将后端搜索结果和 管理端配置的选项进行交集
         */
        result = {
          options: isEmpty(params?.[0]?.filters) ? configOptions : intersectionBy(configOptions, options, 'value'),
          allData: true,
        };
        /**
         * 情况2 和 情况4 中 value的取值 计算逻辑
         *
         * 虚拟版本配置的筛选器的 value值已经在 DashboardLayout/hooks.ts 文件中 计算filters时进行了 交集处理, 所以这里不需要再处理
         */
      }

      if (!isEmpty(params?.[0]?.filters)) { // 说明是 级联子筛选器的选项请求

        // const newValue = {...stringValue, value: [], is_full: !isEmpty(result?.options ?? options)};
        // setStringValue(newValue);
        // innerFilterValueMapRef.current[i] = Object.assign(innerFilterValueMapRef.current?.[i] ?? {}, newValue);

        // 间接级联触发
        if (relevantFilters?.length > 0) {
          relevantFilters.map(child => {
            // 父筛选器变更 触发事件, 子筛选器的唯一标识符i作为事件名称
            eventEmitter.emit(child?.i, filterInfo);
          });
        }

      }
      return result;
    },
    manual: true,
  });

  useEffect(() => {
    // 初始化, 默认值
    setStringValue({
      value: (filterInfo?.isFull ?? true) ? [] : (filterInfo?.defaultValue ?? []),
      is_full: filterInfo?.isFull ?? true,
      operation: filterInfo?.defaultOperation ?? 'include',
      customValues: filterInfo?.defaultCustomValues ?? [],
    });

    // 记录“进入页面/编辑器时”的参数快照，用于 Reset 恢复
    const initialParams = {
      ...params,
      filters: (params.filters ?? []).map((item) => ({...item})),
    };
    initParamsRef.current = initialParams;

    // 初始化选项请求使用初始参数
    getStringOptions({...initialParams});
  }, [filterInfo]);
  
  useEffect(() => {
  
    // 有依赖的父级筛选器时
    if (dependFilters?.length > 0) {
      
      const handleParentEventEmit = () => {
        // isInitRef.current = false;
        getStringOptionsAsync().catch(e => {
          console.log("触发报表子筛选器选项请求: ", e, filterName);
        });
      };
      
      // 注册监听事件, 子筛选器的唯一标识符i作为事件名称
      eventEmitter.on(i, handleParentEventEmit);
  
      return () => {
        // 清除监听器，防止内存泄漏
        eventEmitter.off(i, handleParentEventEmit);
      };
    }
    
  }, [dependFilters]);

  useEffect(() => {
    /**
     * 级联子筛选器场景下，选项必须严格以后端返回为准。
     *
     * 1）当父级筛选变化触发子筛选器重新请求选项时，如果历史选中值中包含后端本次未返回的值
     *    （例如历史为 [a,b,c]，但后端在当前级联条件下只返回 [a,b,d,e]），此前的实现会为了“回显”
     *    从本地 value 中补出一个额外的 c 选项，导致 UI 中出现后台已不可选的值。
     *
     * 2）当级联子筛选器在当前父级条件下后端一个可选值都不返回（options 为空数组）时，
     *    需求要求“以后端结果为准”，不能继续展示历史选中值，因此这里会：
     *      - 清理本地和 innerFilterValueMapRef 中无效的历史选中值；
     *      - 通过 setErrorInfo 设置 code=90000，提示当前级联条件下没有可选项。
     *
     * 为了符合“选项以后端为准，只展示有效选项”这一需求，这里在每次级联请求（lastParams.filters 非空）
     * 且当前为子筛选器（存在 dependFilters）时，用本次返回的 options 对本地选中值做一次交集，
     * 只保留仍然存在于后端 options 中的值，并同步回 innerFilterValueMapRef，防止下游组件再根据旧 value
     * 补出额外选项；在 options 为空时，则直接清空具体选中值并标记 90000 业务错误码。
     */
    if (isEmpty(dependFilters)) {
      return;
    }

    if (!lastParams || isEmpty(lastParams?.filters)) {
      // 非级联请求（初始化等）保持原有行为
      return;
    }

    /**
     * 子筛选器在“全部(is_full=true)”状态下，StringSelectorWithTag 会在 options 首次返回时，
     * 用 options 计算出 allValues 并写回 value（用于 tag/交互展示）。
     * 但当父级筛选变化导致子筛选器重新请求 options 时，旧的 value 会阻止其再次用新 options 计算 allValues，
     * 从而出现“仍保留上次 value”的问题。
     *
     * 需求：子筛选器 + is_full=true 时，每次级联重请求后，都用本次后端返回的 options 替换 value。
     * 实现：当 value 与当前 options 不一致时，先将 value 清空，让 StringSelectorWithTag 重新回填 allValues。
     */
    const currentStringValue = stringValueRef.current;
    
    if (currentStringValue?.is_full) {
      setStringValue((prev) => ({...prev, value: [], is_full: true}));
      innerFilterValueMapRef.current[i] = Object.assign(innerFilterValueMapRef.current?.[i] ?? {}, {
        value: [],
        is_full: true,
      });
      return;
    }
    
    if (!options || options.length === 0) {
      return;
    }

    // if (options.length === 0) {
    //   // 级联子筛选器 + 当前父级条件下后端未返回任何选项
    //   // 无论之前是否为全选或有历史选中值，都要清空具体选中值并关闭全选态，
    //   // 让 UI 层以占位符形式展示（而不是继续显示“全部”或历史值）。
    //   const newStringValue = {
    //     ...stringValue,
    //     value: [],
    //     is_full: false,
    //   };
    //   setStringValue(newStringValue);
    //   innerFilterValueMapRef.current[i] = Object.assign(innerFilterValueMapRef.current?.[i] ?? {}, newStringValue);
    //   setErrorInfo?.({ code: 900000 });
    //   return;
    // }

    if (isEmpty(currentStringValue?.value)) {
      // 本身无具体选中值时，不需要做交集处理
      return;
    }

    const optionValueSet = new Set(options.map((item: CheckOptionType) => item.value));
    const filteredValues = (currentStringValue?.value ?? []).filter((item: string) => optionValueSet.has(item));

    if (filteredValues.length === (currentStringValue?.value ?? []).length) {
      // 没有无效历史值，无需更新
      return;
    }
    
    if (filteredValues.length === 0) {
      setErrorInfo?.({ code: 900000 });
    }
    
    setStringValue((prev) => ({...prev, value: filteredValues, is_full: false}));
    innerFilterValueMapRef.current[i] = Object.assign(innerFilterValueMapRef.current?.[i] ?? {}, {
      value: filteredValues,
      is_full: false,
    });
  }, [dependFilters, options]);
  
  useEffect(() => {
    // 由于 React.memo 用filterInfo进行对比判断, 所以在点击Reset按钮时, 无法触发StringFilter组件的更新渲染
    // 这里采用 订阅事件的方式 在点击Reset时, 触发StringFilter的更新
    const resetValue = (innerValueMap) => {

      if (!isEmpty(dependFilters)) { // 如果有依赖关系的子筛选器,
        const resetParams = initParamsRef.current || {
          ...params,
          filters: (params.filters ?? []).map((item) => ({...item})),
        };
        getStringOptions({...resetParams});
      } else {
        // 非级联筛选器, 重置时 不需要重新请求选项 选项重置
        onSearchKeywordChange('');
      }
      
      // 选项值重置
      setStringValue({
        value: innerValueMap?.[i]?.value ?? [],
        is_full: innerValueMap?.[i]?.is_full ?? false,
        operation: innerValueMap?.[i]?.operation ?? 'include',
        customValues: innerValueMap?.[i]?.customValues ?? [],
      });
    };
    eventEmitter.on('reset', resetValue);
  
    return () => {
      eventEmitter.off('reset', resetValue);
    };
  }, []);
  
  // 筛选器显示优化：增加(包含/排除)文案
  // const operationName = useMemo(() => {
  //   const operation = formatMessage({ id: `dbl_common_${  stringValue?.operation || 'include'}` });
  //   const operationName = `(${operation})`;
  //   return operationName;
  // },[formatMessage, stringValue?.operation]);
  
  return <StringSelectorWithTag
    dropDownRef={ref}
    options={options}
    loading={loading}
    error={error}
    operation={stringValue?.operation}
    onOperationChange={(operation) => {
      setStringValue({...stringValue, operation});
    }}
    isClearSearchWord={isClearSearchWord}
    isAllState={stringValue?.is_full}
    values={stringValue?.value}
    onCheckChange={(value, newIsFull: boolean, customValues) => {
      setStringValue((prev) => ({
        ...prev,
        value,
        is_full: newIsFull,
        customValues,
      }));
    }}
    name={filterName}
    // extName={operationName}
    isExclude={stringValue?.operation === 'exclude'}
    key={i}
    onSearchKeywordChange={onSearchKeywordChange}
    onOpen={() => {
      setOpenStatus(true);
      setVisibleScope(false);
    }}
    onClose={(isChange) => {
      setOpenStatus(false);
      const initFilterValue = innerFilterValueMapRef.current?.[i] ?? {};
      const newValue = stringValueRef.current;

      if (isChange || (newValue.operation !== initFilterValue.operation)) {
        innerFilterValueMapRef.current[i] = Object.assign(initFilterValue, newValue);
        if (relevantFilters?.length > 0) {
          relevantFilters.map(child => {
            // 父筛选器变更 触发事件, 子筛选器的唯一标识符i作为事件名称
            eventEmitter.emit(child?.i, filterInfo);
          });
        }
      }
    }}
  />;
};

export default React.memo(React.forwardRef(StingFilter), (prev, next) => prev.filterInfo === next.filterInfo);